import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useNavigate, useParams } from "react-router-dom"
import OpportunitySkills from "./opportunity-skills"
import Summary from "./summary"
import { InfoText } from "../../common/components/info_text"
import { ExpertiseLevel } from "../../common/components/controls/select/experticeLevel"
import { LoadingPage } from "../../common/components/loading/loadingPage"
import { useUser } from "../../common/hooks/User"
import { getOpportunity, createUserPostulation } from "../../common/apis/opportunities.api"
import { ISubscriptionAnswer, ISubscriptionForm } from "../../common/constants/app.dto"
import { createUserAddress, patchUserAddress, patchUserProfile } from "../../common/apis/private.api"
import { textChooser } from "../../common/util/textChooser"
import { useValidateProfile } from "../../common/hooks/useValidProfile"
import "./apply.scss"

export const Apply = () => {
  const [isLoadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<String>('skills');
  const [summaryData, setSummaryData] = useState<any>({});
  const [validateProfileError, setValidateProfileErro] = useState(false);

  const { getDataAll } = useUser();
  const { register, handleSubmit, setValue, control } = useForm();
  const { cid, oid } = useParams();

  const navigate = useNavigate();
  const userData = getDataAll();
  let { isLoading: isLoadingValidateProfile, refetch } = useValidateProfile(userData.email)

  const client = useQueryClient()

  const { data: knowledges, isLoading: knowledgesLoading } = useQuery("getOpportunityQ", () => getOpportunity(cid!, oid!));

  const [disabledLevels, setDisabledLevels] = useState<string[]>([]);
  const [errors, setErros] = useState<string[]>([]);

  const { mutateAsync, isError , isSuccess, isLoading: isLoadingApply } = useMutation(
    "applyToOpportunity", (params: any) => (createUserPostulation(cid!, oid!, params))
  )

  const { mutateAsync: mutateAsyncUpdateUser, isLoading, isError: hasErrorInUpdateUser } = useMutation(
    'updateProfile', (params: any) => handleInfoAndAddressCreation(params)
  );

  const onSelectionChange = (attrName: string, index: number, value: boolean) => {
    const nameLevel = `${attrName}_${index}`;
    const fieldLevel = `${attrName}[${index}.value]`;

    // this variables holds the list of select items to be disabled in case NO is checked
    const filteredLevels = disabledLevels.filter(
      (item) => item !== nameLevel
    );

    if (!value) {
      setValue(fieldLevel, '0');
      filteredLevels.push(nameLevel);
    }

    setDisabledLevels(filteredLevels);
  }

  const isLevelDisable = (attrName: string, index: number) => (
    disabledLevels.includes(`${attrName}_${index}`)
  )

  const formDataErrors = (data: ISubscriptionForm) => {
    let errorArray: string[] = [];

    Object.keys(data).forEach((key) => {
      const section = (data as any)[key];

      if (!Array.isArray(section)) return;
      (data as any)[key].forEach((obj: ISubscriptionAnswer, index: number) => {
        // if user select "YES" but does not specify "level"
        if (obj.knowledge === true && !obj.value) {
          errorArray.push(`${key}${index}`);
        }
        // if no radio button has been selected
        if (obj.knowledge === null) {
          errorArray.push(`radio.${key}${index}`);
        }
      })
    });
    return errorArray;
  }

  const parseBooleanData = (data: ISubscriptionForm) => {
    Object.keys(data).forEach(key => {
      const section = (data as any)[key];

      if (!Array.isArray(section)) return;

      (data as any)[key].forEach(({ knowledge }: any, index: number) => {
        if (knowledge) {
          (data as any)[key][index].knowledge = knowledge === 'true'
        }
      })
    });
    return data;
  }

  const handleInfoAndAddressCreation = (params: any) => {
    const requestInfo = patchUserProfile(userData.id, params.userInfo);

    const requestAddrs = userData.addresses.length ?
      patchUserAddress(userData.id, userData.addresses[0].id, params.userAddress) :
      createUserAddress(userData.id, params.userAddress)

    return Promise.all([requestInfo, requestAddrs]);
  }

  async function handleUpdateUserProfile({ addresses, ...information }: any) {
    let secondaryStudy = false;
    if (information.secondaryStudy === 'true') {
      secondaryStudy = true
    }

    delete information.technicalKnowledge;
    delete information.technicalKnowledgeIt;
    delete information.languages;

    const salaryRange = parseInt(information.salaryRange)

    await mutateAsyncUpdateUser({
      userAddress: addresses, userInfo: {
        ...information,
        email: userData.email,
        cellPhone: userData.cellPhone,
        id: userData.id,
        secondaryStudy,
        salaryRange
      }
    });

    await client.invalidateQueries(['validate-profile'])
  }

  const getFormInfo = async (data: any) => {
    setLoadingSubmit(true);

    const parsedData = parseBooleanData(data);
    const errorData = formDataErrors(parsedData);

    // if there are any errors set state otherwise reset errors array
    setErros(errorData.length ? errorData : []);

    // making sure all questions have been asked
    if (errorData.length) {
      setLoadingSubmit(false);
      return;
    };

    // forcing user to upload CV
    if(!userData.curriculumDocumentUrl){
      setLoadingSubmit(false);
      setErros([...errors, 'validate-cv']);
      return;
    }

    await handleUpdateUserProfile(data);
    const response = await refetch();

    if (!response.data) {
      setErros([...errors, 'validate-profile']);
      setValidateProfileErro(true);
      setLoadingSubmit(false);
      return;
    }

    setSummaryData(data);
    setCurrentStep('summary');
    setLoadingSubmit(false);
  }

  const handleSubscription = async () => {
    await mutateAsync({
      ...summaryData,
      customerId: userData.id,
      customerName: `${userData.name} ${userData.lastName}`,
      customerEmail: userData.email,
    })

    client.invalidateQueries(['validate-profile']);
    setLoadingSubmit(false);
  }

  useEffect(() => {
    if(isSuccess || isError) {
      navigate(`/apply/verify-subscription/${isSuccess || isError}`);
    }
  }, [isError, isSuccess]);

  const OpportunityForm = () => (
    <>
      <br />
      {/* Conocimientos Tecnicos */}
      {!!knowledges!.technicalKnowledge.length && (
        <div className="knowledge-section">
          <h4 style={{marginBottom: "12px"}}>{textChooser("txt168")}</h4>
          {knowledges!.technicalKnowledge.map((item2, index: number) =>
            <div key={`${item2.name}${index}`}>
              <p>{item2.name}</p>
              <div className="row isCentered">
                <input type={'hidden'} value={item2.type} {...register(`technicalKnowledge[${index}.type]`)} />
                <input type={'hidden'} value={item2.name} {...register(`technicalKnowledge[${index}.name]`)} />
                <input type={'hidden'} value={"-1"} {...register(`technicalKnowledge[${index}.typeId]`)} />

                <div className="col-xs-2 isCentered">
                  <input
                    {...register(`technicalKnowledge[${index}.knowledge]`)}
                    onChange={() => onSelectionChange('technicalKnowledge', index, true)}
                    className={'control-radio'}
                    value={'true'}
                    type={'radio'}
                  />
                  {textChooser("txt111")}
                </div>
                <div className="col-xs-2 isCentered">
                  <input
                    {...register(`technicalKnowledge[${index}.knowledge]`)}
                    onChange={() => onSelectionChange('technicalKnowledge', index, false)}
                    className={'control-radio'}
                    value={'false'}
                    type={'radio'}
                  />
                  {textChooser("txt112")}
                </div>
                <div className="col-xs-1">
                  <b className="text sizeSm">
                    {textChooser("txt145")}
                  </b>
                </div>
                <div className="col-xs-6">
                  <ExpertiseLevel
                    hook={register(`technicalKnowledge[${index}.value]`)}
                    disabled={isLevelDisable('technicalKnowledge', index)}
                    hasError={errors.includes(`technicalKnowledge${index}`)}
                    label=""
                    hasIcon
                  />
                </div>
              </div>
              {errors.includes(`radio.technicalKnowledge${index}`) && (
                <InfoText text={textChooser("txt173")} type="danger" />
              )}
            </div>
          )}
        </div>
      )}

      {/* Conocimientos Ofimatica */}
      {!!knowledges!.technicalKnowledgeIt.length && (
        <div className="knowledge-section">
          <h4 style={{marginBottom: "12px"}}>{textChooser("txt194")}</h4>
          {knowledges!.technicalKnowledgeIt.map((item3, index: number) => 
            <div key={`${item3.name}${index}`}>
              <p>{item3.name}</p>
              <div className="row isCentered">
                {/* this are default preset values */}
                <input type={'hidden'} value={item3.type} {...register(`technicalKnowledgeIt[${index}.type]`)} />
                <input type={'hidden'} value={item3.name} {...register(`technicalKnowledgeIt[${index}.name]`)} />
                <input type={'hidden'} value={"-1"} {...register(`technicalKnowledgeIt[${index}.typeId]`)} />

                <div className="col-xs-2 isCentered">
                  <input
                    {...register(`technicalKnowledgeIt[${index}.knowledge]`)}
                    onChange={() => onSelectionChange('technicalKnowledgeIt', index, true)}
                    className={'control-radio'}
                    value={'true'}
                    type={'radio'}
                  />
                  {textChooser("txt111")}
                </div>
                <div className="col-xs-2 isCentered">
                  <input
                    {...register(`technicalKnowledgeIt[${index}.knowledge]`)}
                    onChange={() => onSelectionChange('technicalKnowledgeIt', index, false)}
                    className={'control-radio'}
                    value={'false'}
                    type={'radio'}
                  />
                  {textChooser("txt112")}
                </div>
                <div className="col-xs-1">
                  <b className="text sizeSm">
                    {textChooser("txt145")}
                  </b>
                </div>
                <div className="col-xs-6">
                  <ExpertiseLevel
                    hook={register(`technicalKnowledgeIt[${index}.value]`)}
                    disabled={isLevelDisable('technicalKnowledgeIt', index)}
                    hasError={errors.includes(`technicalKnowledgeIt${index}`)}
                    hasIcon
                    label=""
                  />
                </div>
              </div>
              {errors.includes(`radio.technicalKnowledgeIt${index}`) && (
                <InfoText text={textChooser("txt173")} type="danger" />
              )}
            </div>
          )}
        </div>
      )}

      {/* Idiomas */}
      {!!knowledges!.languages.length && (
        <div className="knowledge-section">
          <h4 style={{marginBottom: "12px"}}>{textChooser("txt170")}</h4>
          {knowledges!.languages.map((item4, index: number) => 
            <div key={`${item4.name}${index}`}>
              <p>{item4.name}</p>
              <div className="row isCentered">
                {/* this are default preset values */}
                <input type={'hidden'} value={item4.type} {...register(`languages[${index}.type]`)} />
                <input type={'hidden'} value={item4.name} {...register(`languages[${index}.name]`)} />
                <input type={'hidden'} value={"-1"} {...register(`languages[${index}.typeId]`)} />

                <div className="col-xs-2 isCentered">
                  <input
                    {...register(`languages[${index}.knowledge]`)}
                    onChange={() => onSelectionChange('languages', index, true)}
                    className={'control-radio'}
                    value={'true'}
                    type={'radio'}
                  />
                  {textChooser("txt111")}
                </div>
                <div className="col-xs-2 isCentered">
                  <input
                    {...register(`languages[${index}.knowledge]`)}
                    onChange={() => onSelectionChange('languages', index, false)}
                    className={'control-radio'}
                    value={'false'}
                    type={'radio'}
                  />
                  {textChooser("txt112")}
                </div>
                <div className="col-xs-1">
                  <b className="text sizeSm">
                    {textChooser("txt145")}
                  </b>
                </div>
                <div className="col-xs-6">
                  <ExpertiseLevel
                    hook={register(`languages[${index}.value]`)}
                    disabled={isLevelDisable('languages', index)}
                    hasError={errors.includes(`languages${index}`)}
                    hasIcon
                    label=""
                  />
                </div>
              </div>
              {errors.includes(`radio.languages${index}`) && (
                <InfoText text={textChooser("txt173")} type="danger" />
              )}
            </div>
          )}
        </div>
      )}
    </>
  )

  const skillsProps = {
    handleSubmit: handleSubmit(getFormInfo),
    renderOpportunityForm: OpportunityForm,
    isError: isError || hasErrorInUpdateUser,
    isLoading: knowledgesLoading || isLoadingSubmit || isLoading || isLoadingValidateProfile,
    opportunity: knowledges,
    errors,
    register: register,
    setValue,
    validateProfileError,
    control
  }

  const summaryProps = {
    handleSubscription,
    setCurrentStep,
    summaryData,
    isLoading: isLoadingApply,
  }

  function renderStep() {
    switch (currentStep) {
      case 'skills':
        return <OpportunitySkills {...skillsProps} />

      case 'summary':
        return <Summary {...summaryProps} />

      default: return <div />
    }
  };

  return knowledgesLoading ? <LoadingPage /> : renderStep()
}